<template>
  <div>
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Cash Flow Statement</h5>
        <p class="page-sub">Statement of cash flows (IAS 7 — indirect method)</p>
      </div>
      <div class="page-actions">

        <div class="date-range">
          <div class="date-field">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.start_date"
              :enableTimePicker="false"
              :hideInputIcon="true"
              class="date-picker"
              placeholder="Start date"
            />
          </div>
          <span class="date-sep">—</span>
          <div class="date-field">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.end_date"
              :enableTimePicker="false"
              :hideInputIcon="true"
              class="date-picker"
              placeholder="End date"
            />
          </div>
        </div>

        <button class="btn-primary-action" @click="fetchData()" :disabled="crudLoading">
          <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <font-awesome-icon v-else icon="search" class="me-1" />
          {{ crudLoading ? 'Loading...' : 'Search' }}
        </button>

        <div class="dropdown">
          <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" href="#" @click.prevent="exportPDF('cash_flow_statement')">
              <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
            </a></li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="report-wrap">
        <div class="skeleton skeleton--letterhead mb-4"></div>
        <div class="skeleton skeleton--sechdr mb-2"></div>
        <div class="skeleton skeleton--row mb-2" v-for="n in 4" :key="'o'+n"></div>
        <div class="skeleton skeleton--subtotal mb-4 mt-2"></div>
        <div class="skeleton skeleton--sechdr mb-2"></div>
        <div class="skeleton skeleton--row mb-2" v-for="n in 3" :key="'i'+n"></div>
        <div class="skeleton skeleton--subtotal mt-2 mb-4"></div>
        <div class="skeleton skeleton--sechdr mb-2"></div>
        <div class="skeleton skeleton--row mb-2" v-for="n in 2" :key="'f'+n"></div>
        <div class="skeleton skeleton--total mt-3"></div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="!hasData" class="data-card">
      <div class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="water" />
        </div>
        <p class="state-title">No report data</p>
        <p class="state-sub">Select a date range and click Search to generate the cash flow statement.</p>
      </div>
    </div>

    <!-- Report -->
    <div v-else class="report-wrap" ref="pdfContent">

      <!-- Letterhead -->
      <div class="report-letterhead">
        <div class="letterhead-left">
          <div class="letterhead-icon">
            <font-awesome-icon icon="water" />
          </div>
          <div>
            <h2 class="letterhead-title">Cash Flow Statement</h2>
            <p class="letterhead-sub">
              {{ formatDate(tableData.start_date) }} — {{ formatDate(tableData.end_date) }}
            </p>
          </div>
        </div>
        <div class="letterhead-right">
          <div class="cash-summary-cards">
            <div class="cash-card">
              <div class="cash-card-label">Opening Cash</div>
              <div class="cash-card-value">{{ fmtAmt(report.opening_cash) }}</div>
            </div>
            <div class="cash-card cash-card--net" :class="report.net_cash_flow >= 0 ? 'cash-card--pos' : 'cash-card--neg'">
              <div class="cash-card-label">Net Cash Flow</div>
              <div class="cash-card-value">
                <font-awesome-icon :icon="report.net_cash_flow >= 0 ? 'arrow-up' : 'arrow-down'" class="me-1" />
                {{ fmtAmt(Math.abs(report.net_cash_flow)) }}
              </div>
            </div>
            <div class="cash-card cash-card--closing">
              <div class="cash-card-label">Closing Cash</div>
              <div class="cash-card-value">{{ fmtAmt(report.closing_cash) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── SECTION 1: Operating ── -->
      <div class="cf-section">
        <div class="cf-section-header">
          <div class="cf-section-dot cf-section-dot--blue"></div>
          <span class="cf-section-title">Operating Activities</span>
        </div>

        <table class="rep-table">
          <tbody>
            <tr class="cf-base-row">
              <td>Net Profit / (Loss) for the period</td>
              <td class="tr fw6" :class="report.operating.net_profit >= 0 ? 'pos-val' : 'neg-val'">
                {{ fmtAmt(report.operating.net_profit) }}
              </td>
            </tr>
            <tr v-if="report.operating.depreciation !== 0" class="cf-adj-row">
              <td>
                <span class="adj-label">Add: Depreciation (non-cash)</span>
              </td>
              <td class="tr fw6 pos-val">{{ fmtAmt(report.operating.depreciation) }}</td>
            </tr>

            <template v-if="report.operating.working_capital.length">
              <tr class="cf-subhdr-row">
                <td colspan="2" class="cf-subhdr">Changes in Working Capital</td>
              </tr>
              <tr v-for="row in report.operating.working_capital" :key="row.code" class="cf-adj-row">
                <td>
                  <span class="adj-label">{{ row.name }}</span>
                  <span class="acc-code-sm">{{ row.code }}</span>
                </td>
                <td class="tr fw6" :class="row.amount >= 0 ? 'pos-val' : 'neg-val'">
                  {{ fmtAmtSigned(row.amount) }}
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr class="subtotal-row">
              <td class="subtotal-label">Net Cash from Operating Activities</td>
              <td class="tr subtotal-amount" :class="report.operating.total >= 0 ? 'pos-val' : 'neg-val'">
                {{ fmtAmtSigned(report.operating.total) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── SECTION 2: Investing ── -->
      <div class="cf-section">
        <div class="cf-section-header">
          <div class="cf-section-dot cf-section-dot--amber"></div>
          <span class="cf-section-title">Investing Activities</span>
        </div>

        <table class="rep-table">
          <tbody>
            <tr v-if="!report.investing.items.length">
              <td colspan="2" class="muted-cell">No investing activity for this period</td>
            </tr>
            <tr v-for="row in report.investing.items" :key="row.code" class="cf-adj-row">
              <td>
                <span class="adj-label">{{ row.name }}</span>
                <span class="acc-code-sm">{{ row.code }}</span>
              </td>
              <td class="tr fw6" :class="row.amount >= 0 ? 'pos-val' : 'neg-val'">
                {{ fmtAmtSigned(row.amount) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="subtotal-row">
              <td class="subtotal-label">Net Cash from Investing Activities</td>
              <td class="tr subtotal-amount" :class="report.investing.total >= 0 ? 'pos-val' : 'neg-val'">
                {{ fmtAmtSigned(report.investing.total) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── SECTION 3: Financing ── -->
      <div class="cf-section">
        <div class="cf-section-header">
          <div class="cf-section-dot cf-section-dot--green"></div>
          <span class="cf-section-title">Financing Activities</span>
        </div>

        <table class="rep-table">
          <tbody>
            <tr v-if="!report.financing.items.length">
              <td colspan="2" class="muted-cell">No financing activity for this period</td>
            </tr>
            <tr v-for="row in report.financing.items" :key="row.code" class="cf-adj-row">
              <td>
                <span class="adj-label">{{ row.name }}</span>
                <span class="acc-code-sm">{{ row.code }}</span>
              </td>
              <td class="tr fw6" :class="row.amount >= 0 ? 'pos-val' : 'neg-val'">
                {{ fmtAmtSigned(row.amount) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="subtotal-row">
              <td class="subtotal-label">Net Cash from Financing Activities</td>
              <td class="tr subtotal-amount" :class="report.financing.total >= 0 ? 'pos-val' : 'neg-val'">
                {{ fmtAmtSigned(report.financing.total) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── Net Totals ── -->
      <div class="net-block">
        <div class="net-row">
          <span class="net-label">Net Increase / (Decrease) in Cash</span>
          <span class="net-val" :class="report.net_cash_flow >= 0 ? 'pos-val' : 'neg-val'">
            {{ fmtAmtSigned(report.net_cash_flow) }}
          </span>
        </div>
        <div class="net-row net-row--border">
          <span class="net-label">Cash at Beginning of Period</span>
          <span class="net-val">{{ fmtAmt(report.opening_cash) }}</span>
        </div>
        <div class="net-row net-row--total">
          <span class="net-label">Cash at End of Period</span>
          <span class="net-val" :class="report.closing_cash >= 0 ? 'pos-val' : 'neg-val'">
            {{ fmtAmt(report.closing_cash) }}
          </span>
        </div>
      </div>

      <div class="rep-footer">Generated by Mapiibs Solution</div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../../lib/reusableMixin';
import ReportHeaderTab from './components/reportheadertab.vue';
import moment from 'moment';

export default {
  components: { ReportHeaderTab },
  mixins: [reusableMixin],

  data() {
    return {
      report:      null,
      loading:     false,
      crudLoading: false,

      tableData: { page: 1,
        start_date: new Date(new Date().getFullYear(), 0, 1),  // Jan 1 this year
        end_date:   new Date(),
      },

      toast: useToast(),
    };
  },

  computed: {
    hasData() { return this.report !== null; },
  },

  created() { this.fetchData(); },

  methods: {
    fetchData() {
      this.loading     = true;
      this.crudLoading = true;

      const params = {
        start_date: moment(this.tableData.start_date).format('YYYY-MM-DD'),
        end_date:   moment(this.tableData.end_date).format('YYYY-MM-DD'),
      };

      axios.get('/accounting/report/cash-flow', { params })
        .then(response => {
          this.report = response.data.data;
        })
        .catch(error => {
          if ([400, 401, 403, 500].includes(error.response?.status)) {
            this.toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
        });
    },

    fmtAmt(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    fmtAmtSigned(value) {
      const abs = Math.abs(value ?? 0);
      const formatted = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return value < 0 ? `(${formatted})` : formatted;
    },

    formatDate(date) { return moment(date).format('DD/MM/YYYY'); },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* ── Date Range ── */
.date-range { display:flex; align-items:center; gap:6px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:4px 10px; }
.date-field { position:relative; display:flex; align-items:center; }
.date-icon  { position:absolute; left:8px; color:#7a90b0; font-size:12px; z-index:2; pointer-events:none; }
.date-sep   { color:#aab8cc; font-size:13px; flex-shrink:0; }
:deep(.date-picker)       { border:none !important; background:transparent !important; box-shadow:none !important; }
:deep(.date-picker input) { border:none !important; font-size:13px; color:#1a2b45; padding:4px 4px 4px 24px !important; width:130px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline::after { display:none !important; }
.me-1 { margin-right:4px; } .me-2 { margin-right:8px; }

/* ── Skeleton ── */
.skeleton     { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--letterhead { height:90px; }
.skeleton--sechdr     { height:32px; width:220px; }
.skeleton--row        { height:18px; }
.skeleton--subtotal   { height:24px; width:55%; margin-left:auto; }
.skeleton--total      { height:28px; width:45%; margin-left:auto; }
.mb-2 { margin-bottom:8px; } .mb-4 { margin-bottom:16px; } .mt-2 { margin-top:8px; } .mt-3 { margin-top:12px; }

/* ── Empty ── */
.data-card   { background:#fff; border:1px solid #e4eaf2; border-radius:12px; }
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }

/* ── Report Wrap ── */
.report-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:32px; }

/* ── Letterhead ── */
.report-letterhead { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; padding-bottom:24px; border-bottom:2px solid #e4eaf2; margin-bottom:24px; flex-wrap:wrap; }
.letterhead-left   { display:flex; align-items:center; gap:16px; }
.letterhead-icon   { width:52px; height:52px; border-radius:14px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
.letterhead-title  { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 3px; }
.letterhead-sub    { font-size:13px; color:#7a90b0; margin:0; }
.letterhead-right  { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }

/* ── Cash summary cards ── */
.cash-summary-cards { display:flex; gap:10px; flex-wrap:wrap; }
.cash-card          { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:10px 16px; min-width:130px; }
.cash-card-label    { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:4px; }
.cash-card-value    { font-size:15px; font-weight:800; color:#1a2b45; }
.cash-card--pos .cash-card-value  { color:#16a34a; }
.cash-card--neg .cash-card-value  { color:#dc2626; }
.cash-card--closing .cash-card-value { color:#004AAD; }

/* ── CF Sections ── */
.cf-section { margin-bottom:28px; }

.cf-section-header { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.cf-section-dot    { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.cf-section-dot--blue  { background:#004AAD; }
.cf-section-dot--amber { background:#d97706; }
.cf-section-dot--green { background:#16a34a; }
.cf-section-title  { font-size:14px; font-weight:700; color:#1a2b45; }

/* ── Table ── */
.rep-table { width:100%; border-collapse:collapse; }

.cf-base-row td    { padding:10px 14px; font-size:13.5px; border-bottom:1px solid #f0f4f8; }
.cf-adj-row  td    { padding:8px 14px 8px 28px; font-size:13px; color:#4a5e78; border-bottom:1px solid #f8fafc; }
.cf-subhdr-row td  { padding:8px 14px; }
.cf-subhdr         { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#94a3b8; }

.adj-label  { display:inline-block; }
.acc-code-sm { font-family:monospace; font-size:10.5px; color:#94a3b8; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:4px; padding:0px 5px; margin-left:8px; }

.subtotal-row    { background:#f4f7fb; border-top:2px solid #e4eaf2; }
.subtotal-row td { padding:11px 14px; }
.subtotal-label  { font-size:13px; font-weight:700; color:#1a2b45; }
.subtotal-amount { font-size:14px; font-weight:800; }

.muted-cell { padding:14px; font-size:13px; color:#aab8cc; }

/* ── Totals ── */
.net-block    { border-top:2px solid #e4eaf2; margin-top:8px; padding-top:16px; display:flex; flex-direction:column; gap:0; }
.net-row      { display:flex; align-items:center; justify-content:space-between; padding:9px 14px; font-size:13.5px; }
.net-row--border { border-top:1px dashed #e4eaf2; }
.net-row--total  { border-top:2px solid #1a2b45; background:#f4f7fb; border-radius:0 0 8px 8px; }
.net-label    { font-weight:600; color:#1a2b45; }
.net-val      { font-weight:800; font-size:15px; color:#1a2b45; }

/* ── Value colours ── */
.pos-val { color:#16a34a; }
.neg-val { color:#dc2626; }
.tr  { text-align:right; }
.fw6 { font-weight:700; }

/* ── Report Footer ── */
.rep-footer { text-align:center; margin-top:28px; padding-top:20px; border-top:1px solid #e4eaf2; font-size:12px; color:#aab8cc; }

/* ── Dropdown ── */
.dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
.dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
.dropdown-item:hover { background:#f4f7fb; }

/* ── Print ── */
@media print {
  .page-header,.btn-primary-action,.btn-outline,.dropdown { display:none !important; }
  .report-wrap { border:none; padding:0; }
}

/* ── Responsive ── */
@media(max-width:767px) {
  .page-header,.page-actions { flex-direction:column; align-items:stretch; }
  .date-range { flex-wrap:wrap; }
  .report-letterhead { flex-direction:column; }
  .letterhead-right  { align-items:flex-start; }
  .cash-summary-cards { flex-direction:column; }
  .report-wrap { padding:16px; }
}
</style>
